// external libs
import axios from 'axios'
import { ofType } from 'redux-observable'
import { from, of, EMPTY } from 'rxjs'
import { switchMap, catchError } from 'rxjs/operators'
import I18n from 'i18n-react'
import { mergeDeepRight } from 'ramda'

import SuriApi from '../../Services/SuriApi'
import GuardXApi from '../Services/GuardXApi'
// redux
import ProductActions, { ProductTypes } from '../Redux/ProductRedux'
import UserDataActions, { UserDataTypes } from '../Redux/UserDataRedux'
import LinkBuilderActions, { LinkBuilderTypes } from '../Redux/LinkBuilderRedux'
import GeneralActions, { ERROR_TYPES, GeneralTypes } from '../Redux/GeneralRedux'
import ValidationActions from '../Redux/ValidationRedux'
import InputHelpsActions from '../Redux/InputHelpsRedux'
import QuoteActions from '../Redux/QuoteRedux'
import NavigationActions from '../Redux/NavigationRedux'
import SettingsActions from '../Redux/SettingsRedux'
import DiscountsActions from '../Redux/DiscountsRedux'
import { initTariffsContent } from '../ContentFunctions'
import { WEB_TYPES, WEB_TYPES_KEYS } from '../Redux/SettingsRedux'
import { LogTypes } from '../Redux/LogRedux'
import FilterActions from '../Redux/FilterRedux'
import QuoteInstance from '../Controllers/QuoteInstance'
import { getValidCalculatorsForProductName } from '../FilterFunctions'

const ProductEpics = [
  (action$) =>
    action$.pipe(
      ofType(LogTypes.SEND_LOG),
      switchMap((action) =>
        from(
          GuardXApi.writeEntry({
            logName: 'projects/suri-179907/logs/my-log',
            entries: [
              {
                jsonPayload: action.log,
                severity: 'WARNING',
              },
            ],
            resource: {
              labels: {
                name: 'suri-fe-log',
              },
              type: 'logging_log',
            },
          }),
        ).pipe(
          switchMap(() => EMPTY),
          catchError(() => EMPTY),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.ALL_PRODUCTS_REQUEST),
      switchMap(() =>
        from(SuriApi.allProducts()).pipe(
          switchMap((response) => {
            if (response.status === 200) {
              return of(ProductActions.allProductsRequestSuccess(response.data._embedded.products))
            }
            throw response
          }),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.PRODUCT_CONTENT_REQUEST_LOADER_OFF),
      switchMap((action) =>
        from(
          action.isBackOffice
            ? SuriApi.productContentBackOffice(action.product)
            : SuriApi.productContent(action.product),
        ).pipe(
          switchMap((response) => {
            const {
              components,
              errorTranslations,
              fieldNames,
              fieldOrder,
              fieldReferences,
              images,
              inputHelps,
              priceIncludesBlacklist,
              quoteRestrictions,
              translations,
              transManufacturer,
              transModel,
              types,
              valueNames,
              valueTranslations,
            } = response.data.data
            const result = [
              ProductActions.productContentRequestSuccess({
                types,
                fieldNames,
                fieldOrder,
                valueNames,
                components,
              }),
              GeneralActions.translationsRequestSuccess({...translations, valueTranslations}),
              GeneralActions.errorTranslationsRequestSuccess(errorTranslations),
            ]
            if (inputHelps) {
              result.push(InputHelpsActions.productInputHelpsMerge(inputHelps))
            }
            if (images) {
              result.push(InputHelpsActions.defaultTemplateVariablesRequestSuccess(images, 'images'))
            }
            if (fieldReferences) {
              result.push(ProductActions.setFieldReferences(fieldReferences))
            }
            if (transManufacturer) {
              result.push(ProductActions.setTransManufacturer(transManufacturer))
            }
            if (transModel) {
              result.push(ProductActions.setTransModel(transModel))
            }
            if (quoteRestrictions) {
              result.push(QuoteActions.setQuoteRestrictions(quoteRestrictions))
            }
            if (priceIncludesBlacklist) {
              result.push(ProductActions.setPriceIncludesBlacklist(priceIncludesBlacklist))
            }

            return from(result)
          }),
          catchError((e) => {
            return from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL),
              NavigationActions.onSetLastValidStepIndex('product'),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.PRODUCT_RESOURCES_REQUEST_LOADER_OFF),
      switchMap((action) =>
        from(SuriApi.productResources(action.product)).pipe(
          switchMap((response) => of(ProductActions.resourcesRequestSuccess(response.data.data))),
          catchError((e) => {
            return from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL),
              NavigationActions.onSetLastValidStepIndex('product'),
            ])
          }),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.PRODUCT_TEXT_CONTENT_REQUEST),
      switchMap((action) =>
        from(SuriApi.productStep4Content(action.productName)).pipe(
          switchMap((response) =>
            of(
              ProductActions.productTextContentRequestSuccess(
                response.data.data.textOrder,
                response.data.data.blocks,
                response.data.data.alerts,
                response.data.data.documents,
              ),
            ),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(UserDataTypes.DEFAULT_VALUES_REQUEST_LOADER_OFF),
      switchMap((action) =>
        from(SuriApi.getDefaultValues(action.productName)).pipe(
          switchMap((response) =>
            from([
              UserDataActions.defaultValuesRequestSuccess({
                ...response.data.data[action.subProductName],
                ...(action.isBackOffice &&
                response.data.data.isBackOffice &&
                response.data.data.isBackOffice[action.subProductName]
                  ? response.data.data.isBackOffice[action.subProductName]
                  : {}),
              }),
              ValidationActions.onChangeValidationSig(true),
            ]),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.GET_TARIFFS_CONTENT_REQUEST),
      switchMap((action) =>
        from(SuriApi.getTariffsContent(action.productName)).pipe(
          switchMap((response) =>
            of(ProductActions.getTariffsContentRequestSuccess(initTariffsContent(response.data.data))),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.GET_ONE_TARIFF_CONTENT_REQUEST),
      switchMap((action) =>
        from(SuriApi.getOneTariffContent(action.productName, action.tariffName)).pipe(
          switchMap((response) =>
            of(
              ProductActions.getTariffsContentRequestSuccess(
                initTariffsContent({ [action.tariffName]: response.data.data }),
              ),
            ),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.GET_TWO_TARIFF_CONTENTS_REQUEST),
      switchMap((action) =>
        from(SuriApi.getTwoTariffContents(action.productName, action.tariffName1, action.tariffName2)).pipe(
          switchMap((response) =>
            of(
              ProductActions.getTariffsContentRequestSuccess(
                initTariffsContent({
                  [action.tariffName1]: response.tariff1.data.data,
                  [action.tariffName2]: response.tariff2.data.data,
                }),
              ),
            ),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.QUOTE_FILTERS_REQUEST),
      switchMap((action) =>
        from(SuriApi.getQuoteFilters(action.product)).pipe(
          switchMap((response) => of(ProductActions.quoteFiltersRequestSuccess(response.data))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(ProductTypes.PRODUCT_DOCUMENTS_REQUEST),
      switchMap((action) =>
        from(SuriApi.getProductDocuments(action.product)).pipe(
          switchMap((response) => of(ProductActions.productDocumentsRequestSuccess(response.data.data || {}))),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.CRITICAL))),
        ),
      ),
    ),
  (action$) =>
    action$.pipe(
      ofType(UserDataTypes.RESTORE_DATA_REQUEST),
      switchMap((action) =>
        from(axios.all([SuriApi.getDefaultValues(action.productName), SuriApi.getDefaultContent()])).pipe(
          switchMap(([responseDefaultData, responseDefaultContent]) => {
            const defaultData = {
              ...responseDefaultData.data.data[action.subProductName],
              ...(action.isBackOffice &&
              responseDefaultData.data.data.isBackOffice &&
              responseDefaultData.data.data.isBackOffice[action.subProductName]
                ? responseDefaultData.data.data.isBackOffice[action.subProductName]
                : {}),
            }
            return from(SuriApi.getContractData(action.linkId)).pipe(
              switchMap((response) => {
                const data = response.data
                let webType = WEB_TYPES.DEFAULT
                for (let index = 0; index < Object.keys(WEB_TYPES_KEYS).length; index++) {
                  if (WEB_TYPES_KEYS[index] === data.website) {
                    webType = index
                    break
                  }
                }
                const actions = [
                  SettingsActions.onChangeWebType(webType),
                  UserDataActions.restoreDataRequestSuccess(data, defaultData, action.linkId),
                  ValidationActions.onChangeValidationSig(true),
                  LinkBuilderActions.setDuplicatedLink(''),
                  UserDataActions.setProperties({
                    caseId: '',
                    crmId: undefined,
                    ...data.properties,
                    itIsDraft: undefined,
                    allDiscountValues: undefined,
                    calculatorName: undefined,
                  }),
                ]
                if (data.affiliate) {
                  actions.push(ProductActions.setAffiliate(data.affiliate))
                }
                let selectedTariffs = data.selectedTariffs
                if (data.properties) {
                  if (data.properties.activeTabKey) {
                    actions.push(
                      QuoteActions.onChangeActiveTab(data.properties.activeTabKey),
                      ValidationActions.onChangeValidationSig(true),
                      QuoteActions.setSignalToChangeTab(data.properties.activeTabKey),
                    )
                  }
                  if (data.properties.filterData) {
                    actions.push(FilterActions.setFilterData(data.properties.filterData))
                  }
                  if (data.properties.enabledCalculators && responseDefaultContent.data.data) {
                    const allEnabled = getValidCalculatorsForProductName(
                      responseDefaultContent.data.data.insuranceCompanies,
                      action.productName,
                      false,
                      true,
                    )
                    actions.push(
                      FilterActions.setEnabledCalculators(
                        data.properties.enabledCalculators.filter((calc) => allEnabled.includes(calc)),
                      ),
                    )
                  }
                  if (data.properties.firstStepCicCompleted) {
                    actions.push(UserDataActions.wasCicCompleted(data.properties.firstStepCicCompleted))
                  }
                  if (data.properties.itIsDraft) {
                    actions.push(LinkBuilderActions.sendLinkSuccess(action.linkId))
                  } else {
                    actions.push(LinkBuilderActions.sendLinkSuccess(''))
                  }
                  if (data.properties.temporaryFilters) {
                    actions.push(
                      QuoteActions.setQuoteFilters(data.properties.temporaryFilters, 'temporaryFilters'),
                      GeneralActions.onStopFetching(),
                    )
                  }
                  if (data.properties.activeQuoteFilters) {
                    actions.push(
                      QuoteActions.setQuoteFilters(data.properties.activeQuoteFilters, 'filters'),
                      GeneralActions.onStopFetching(),
                    )
                  }
                  if (
                    data.properties.steps &&
                    data.properties.lastValidStep &&
                    typeof data.properties.lastValidStep.index !== 'undefined'
                  ) {
                    let stepIndex = data.properties.lastValidStep.index + 1
                    if (stepIndex >= 3) {
                      stepIndex = 2
                    }
                    if (!data.properties.selectedQuote && stepIndex >= 2) {
                      stepIndex = 1
                    }
                    if (!data.properties.steps[stepIndex].url) {
                      stepIndex -= 1
                    }
                    actions.push(
                      NavigationActions.setSteps(data.properties.steps, stepIndex),
                      GeneralActions.onChangeRedirectUrl(data.properties.steps[stepIndex].url),
                    )
                  }
                  if (data.properties.selectedQuote) {
                    const selectedQuote: QuoteInstance = data.properties.selectedQuote
                    actions.push(QuoteActions.saveQuotes([selectedQuote], selectedQuote.tariffKey))
                  }
                  if (data.properties.selectedTariffs) {
                    selectedTariffs = data.properties.selectedTariffs
                  }
                  if (data.properties.selectedCoveragePrices) {
                    actions.push(QuoteActions.setSelectedCoveragePrices(data.properties.selectedCoveragePrices))
                  }
                }
                const discounts =
                  data.properties && data.properties.allDiscountValues
                    ? data.properties.allDiscountValues
                    : data.discountValues || {}
                if (selectedTariffs) {
                  actions.push(QuoteActions.setSelectedQuotes(selectedTariffs))
                  if (data.discountValues && data.properties.calculatorName) {
                    discounts[data.properties.calculatorName] = data.discountValues
                  }
                }
                actions.push(
                  DiscountsActions.setDiscounts(discounts),
                  ValidationActions.onChangeDiscountValidationSig(true),
                )
                if (selectedTariffs && Object.keys(selectedTariffs).length === 1) {
                  actions.push(QuoteActions.onChangeGoToTab(Object.keys(selectedTariffs)[0]))
                }
                return from(actions)
              }),
              catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
            )
          }),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        ),
      ),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(LinkBuilderTypes.SEND_LINK_REQUEST_LOADER_OFF),
      switchMap((action) => {
        const trackingParams = state$.value.userData.trackingParams
        const data = action.data
        if (typeof trackingParams === 'object' && Object.keys(trackingParams).length) {
          data.trackingParams = trackingParams
        }
        return from(
          action.requestType === 'quoteRequest'
            ? SuriApi.updateOrCreateQuoteRequestLink('car', data, action.link)
            : SuriApi.updateOrCreateContractDraftLink('car', data, action.link),
        ).pipe(
          switchMap((response) => {
            return of(LinkBuilderActions.sendLinkSuccess(action.link || response.data.linkId))
          }),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        )
      }),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(LinkBuilderTypes.LINK_REQUEST_WITH_ASYNC_REQUEST_LOADER_OFF),
      switchMap((action) => {
        const trackingParams = state$.value.userData.trackingParams
        const data = action.linkData.data
        if (typeof trackingParams === 'object' && Object.keys(trackingParams).length) {
          data.trackingParams = trackingParams
        }
        return from(SuriApi.getProductLink(data)).pipe(
          switchMap((response) =>
            from([
              QuoteActions.quoteAsyncRequest(
                action.quoteRequestData.product,
                action.quoteRequestData.data,
                action.quoteRequestData.quotesKey,
                action.quoteRequestData.dependsOn,
                action.quoteRequestData.subProductName,
                {
                  linkUrl: response.data._links.ref && response.data._links.ref.href,
                  linkId: response.data.linkId,
                  linkTitle: action.linkData.indices.title,
                },
              ),
              LinkBuilderActions.setProductLinkUrlRequestSuccess(
                response.data._links.ref && response.data._links.ref.href,
                response.data.linkId,
                !!action.linkData.data.properties.itIsDraft,
              ),
            ]),
          ),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        )
      }),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(LinkBuilderTypes.SET_PRODUCT_LINK_URL_REQUEST),
      switchMap((action) => {
        const trackingParams = state$.value.userData.trackingParams
        const data = action.productLinkData
        if (typeof trackingParams === 'object' && Object.keys(trackingParams).length) {
          data.trackingParams = trackingParams
        }
        return from(SuriApi.getProductLink(data)).pipe(
          switchMap((response) => {
            const url = response.data._links.ref.href
            return from([
              LinkBuilderActions.setProductLinkUrlRequestSuccess(
                url,
                response.data.linkId,
                action.productLinkData.properties.itIsDraft,
              ),
              LinkBuilderActions.saveProductLinkRequest({
                ...action.data,
                url,
              }),
            ])
          }),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        )
      }),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(LinkBuilderTypes.SAVE_PRODUCT_LINK_REQUEST),
      switchMap((action) => {
        const trackingParams = state$.value.userData.trackingParams
        const data = action.data
        if (typeof trackingParams === 'object' && trackingParams.crmId) {
          data.crmId = trackingParams.crmId
        }
        return from(SuriApi.saveProductLink(data)).pipe(
          switchMap((response) => of(LinkBuilderActions.saveProductLinkRequestSuccess(response.data[0].errorCode))),
          catchError((e) =>
            from([
              GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING),
              LinkBuilderActions.saveProductLinkRequestSuccess('error'),
            ]),
          ),
        )
      }),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(LinkBuilderTypes.DUPLICATE_REQUEST_LOADER_OFF),
      switchMap((action) => {
        const trackingParams = state$.value.userData.trackingParams
        const data = action.data
        if (typeof trackingParams === 'object' && Object.keys(trackingParams).length) {
          data.trackingParams = trackingParams
        }
        return from(
          action.requestType === 'quoteRequest'
            ? SuriApi.updateOrCreateQuoteRequestLink('car', data, action.link)
            : SuriApi.updateOrCreateContractDraftLink('car', data, action.link),
        ).pipe(
          switchMap((response) => {
            window.open(
              `${window.location.href.replace(action.pathName, '')}?linkId=${response.data.linkId}`,
              response.data.linkId,
            )
            return of(LinkBuilderActions.setDuplicatedLink(response.data.linkId))
          }),
          catchError((e) => of(GeneralActions.onChangeRequestFailure(e, ERROR_TYPES.WARNING))),
        )
      }),
    ),
  (action$, state$) =>
    action$.pipe(
      ofType(GeneralTypes.LOG_TO_STACKDRIVER),
      switchMap((action) => {
        let projectId = 'suri-179907'
        if (typeof document !== 'undefined') {
          // inovis-test
          const projectIdEl = document.getElementById('stackdriverProjectId')
          if (projectIdEl) {
            projectId = projectIdEl.value
          }
        }
        return from(
          SuriApi.log({
            logName: action.logName,
            projectId,
            metadata: {
              resource: {
                type: 'global',
              },
              severity: action.severity || 'INFO',
            },
            data: {
              ...action.data,
              logUuid: state$.value.appData.general.logUuid,
            },
          }),
        ).pipe(
          switchMap(() => EMPTY),
          catchError(() => EMPTY),
        )
      }),
    ),
]

export default ProductEpics
